<script setup>
// Vue twin of <x-chip> (resources/views/components/chip.blade.php +
// App\View\Components\Chip). It emits the same markup and class hooks
// (.chip, .chip_label, .count, .active) and carries no styling of its own:
// components/chip.scss styles both implementations, so Blade- and
// Vue-rendered chips cannot drift apart visually. Server-rendered pages keep
// using <x-chip>; Vue-rendered contexts use this.
//
// Same three shapes, derived from the props passed - never set directly:
//
//   'filter' - name + value given. A <label> wrapping a real (visually
//              hidden) checkbox or radio, so keyboard use and screen-reader
//              announcement come from the native control. State is bound with
//              v-model (the Vue stand-in for Blade's `checked`): a shared
//              string ref for a radio group, a boolean or array for
//              checkboxes - native Vue v-model semantics throughout. The
//              checked look needs no class: chip.scss keys it off
//              :has(input:checked), same as the Blade chip.
//   'link'   - href given. An <a>.
//   'static' - neither. A plain <span> for a read-only tag.
//
// Radio-group note for callers: the inputs are real radios grouped by `name`,
// so a name must not be reused by another group on the same page, and a radio
// cannot be un-picked by clicking it again - an exclusive group normally
// wants an explicit "All" / "Any" chip as its clear mechanism.
//
// Where the Blade class throws on a contradictory call site, this component
// logs a console error and renders the nearest sensible shape - a throw from
// setup() would take down the widget's whole app root, not just one chip.
// Unknown icon names are handled the same way, inside SvgIcon.
//
// $count is optional in every mode and renders after the label. Zero is a
// real value and still shows. Extra attributes (class included) fall through
// to the outer element; `id` is a declared prop because, as in Blade, it
// belongs on the input - pass it only when JS needs to target the input.
import { computed } from 'vue';
import SvgIcon from './SvgIcon.vue';

const props = defineProps({
    label: {
        type: String,
        required: true,
    },
    icon: {
        type: String,
        default: null,
    },
    href: {
        type: String,
        default: null,
    },
    name: {
        type: String,
        default: null,
    },
    value: {
        type: String,
        default: null,
    },
    // undefined doubles as "not bound", for the filter-only misuse warning.
    modelValue: {
        type: [String, Boolean, Array],
        default: undefined,
    },
    radio: {
        type: Boolean,
        default: false,
    },
    active: {
        type: Boolean,
        default: false,
    },
    id: {
        type: String,
        default: null,
    },
    count: {
        type: Number,
        default: null,
    },
});

const emit = defineEmits(['update:modelValue']);

// Shape validation, mirroring the Blade constructor's rules. Run once at
// setup, like the constructor: the shape props are call-site decisions, not
// runtime state.
if (props.href !== null && (props.name !== null || props.value !== null)) {
    console.error(
        '[Chip] A chip cannot be both a link and a filter. '
        + 'Pass href, or name + value, not both. Ignoring href.'
    );
}

if ((props.name === null) !== (props.value === null)) {
    console.error(
        '[Chip] A filter chip needs both name and value; only one was passed. '
        + 'Ignoring it.'
    );
}

const mode = props.name !== null && props.value !== null
    ? 'filter'
    : (props.href !== null ? 'link' : 'static');

if (mode !== 'filter' && (props.radio || props.id !== null || props.modelValue !== undefined)) {
    console.error(
        '[Chip] radio, id and v-model only apply to a filter chip (name + value). '
        + 'For a link or static chip use active. Ignoring them.'
    );
}

if (mode === 'filter' && props.active) {
    console.error(
        '[Chip] active does not apply to a filter chip - its state comes from '
        + 'the input via v-model. Ignoring it.'
    );
}

const inputType = props.radio ? 'radio' : 'checkbox';

// Writable proxy so v-model on the native input gets Vue's own
// radio/checkbox/array handling while the state lives with the caller.
const model = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
});

const classes = computed(() => ['chip', { active: props.active && mode !== 'filter' }]);
</script>

<template>
    <label v-if="mode === 'filter'" :class="classes">
        <input
            v-model="model"
            :type="inputType"
            :name="name"
            :value="value"
            :id="id ?? undefined"
        >
        <SvgIcon v-if="icon" :name="icon" />
        <span class="chip_label">{{ label }}</span>
        <span v-if="count !== null" class="count">{{ count }}</span>
    </label>
    <a v-else-if="mode === 'link'" :class="classes" :href="href">
        <SvgIcon v-if="icon" :name="icon" />
        <span class="chip_label">{{ label }}</span>
        <span v-if="count !== null" class="count">{{ count }}</span>
    </a>
    <span v-else :class="classes">
        <SvgIcon v-if="icon" :name="icon" />
        <span class="chip_label">{{ label }}</span>
        <span v-if="count !== null" class="count">{{ count }}</span>
    </span>
</template>
